/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import './features/sharedcomponents/App/App.scss';

import { LoginCallback, Security } from '@okta/okta-react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { lazy, StrictMode, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from '@mui/material/styles';

import { ColorModeContext, usePaletteMode } from './features/sharedcomponents/App/theme';
import { analyticsIdentify } from './features/sharedcomponents/analytics_utils';
import {
  selectPearlUserInfo,
  selectUserFlags,
  selectUserSurveyInfo,
  selectCanViewCME,
  fetchSurveyInfo,
  fetchNpsSurveyInfo,
  selectNPSSurvey,
} from './features/sharedcomponents/Login/userSlice';
import { AuthConfig } from './features/sharedcomponents/okta_config';
import FavoriteTable from './features/PersonalArea/FavoriteTable';
import CME from './features/PersonalArea/CME';
import { ChatOrHomeRoute } from './ChatOrHomeRoute';
import { ConsoleLog } from './features/sharedcomponents/ConsoleLog/ConsoleLog';
import DevWarningModal from './features/sharedcomponents/Login/DevWarningModal';
import { ErrorScreenContainer } from './features/sharedcomponents/ErrorScreen';
import { LoginCallbackLoading } from './features/sharedcomponents/Login/LoginCallbackLoading';
import LoginErrorComponent from './features/sharedcomponents/Login/LoginErrorComponent';
import { PretendEpicLogin } from './features/sharedcomponents/Epic/PretendEpicLogin';
import { RequiredAuth } from './features/sharedcomponents/Login/SecureRoute';
import SnackbarCloseButton from './features/sharedcomponents/notification/SnackBarCloseButton';
import { SnackbarUtilsConfigurator } from './features/sharedcomponents/notification/SnackBarUtils';
import TopLayout from './features/TopLayout/TopLayout';
import { getSmartFhirCookieData } from './features/sharedcomponents/Epic/smartFhirUtils';
import { setOktaAuth } from './features/sharedcomponents/api/pearlApi';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { useShareVueCookieLogin, useSmartFhirCookieLogin } from './features/sharedcomponents/Login/useHooks';
import { LoginCallbackWrapper } from './features/sharedcomponents/Login/LoginCallbackWrapper';
import HomePage from './features/sharedcomponents/Home/HomePage';
import { SurveyTypes } from './features/sharedcomponents/Login/types';
import LegalDisclaimerSurvey, { NPSSurvey } from './Surveys/SurveyRenderer';
import NPI from './features/NPI/NPI';
import { useCommonAppStartup } from './features/sharedcomponents/App/useHook';
import { AdminFhirDownloadAnonymous } from './features/sharedcomponents/Admin/AdminFhirDownloadAnonymous';
import { PretendShareVueLogin } from './features/sharedcomponents/ShareVue/PretendShareVueLogin';
import { getShareVueCookieData } from './features/sharedcomponents/ShareVue/ShareVueUtils';
import {
  isEnabledAffiliateAdminForUser,
  isMuscUser,
  useEnableEpicEmbeddedView,
} from './features/sharedcomponents/permissions';
import Loading from './features/sharedcomponents/Login/Loading';
import { ExternalReviewerView } from './ExternalReviewer/ExternalReviewerView';
import { NavigateWithFallback } from './features/sharedcomponents/App/NavigateWithFallback';
const AffiliateAdminDashboard = lazy(() => import('./features/AffiliateAdmin/AffiliateAdminDashboard'));

/** Wrapper of <App/> to add a okta auth <Security>
 *  NOTE:
 *   We MUST be careful here.  This will get rendered when BrowserRouter changes,
 *   which will happen when we navigate.
 *   Make sure that we do not create a new OktaAuth, do not start a second one, etc.
 *
 *  Do NOT touch this code without checking with John T.  I have spent soooo many days fighting with this.
 *  And then fixing it when someone else breaks it.
 */
function AppWithOktaSecurityWrapper(oktaAuthConfig: AuthConfig) {
  const smartFhirCookieData = getSmartFhirCookieData();
  const shareVueCookieData = getShareVueCookieData();
  const oktaAuth = useMemo(() => {
    const oktaAuth = new OktaAuth(oktaAuthConfig);
    setOktaAuth(oktaAuth);
    void oktaAuth.start(); // Start a background service to do token autorenew, cross-tab synchronization etc. See okta-auth-js readme
    return oktaAuth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const restoreOriginalUri = useCallback(
    (_oktaAuth: any, originalUri: string) => {
      console.log('AppWithOktaSecurityWrapper navigate() to originalUri', originalUri);
      const sessionStorageRedirectUri = sessionStorage.getItem('redirectUri');
      console.log('sessionStorage remembers it as', sessionStorageRedirectUri);
      setTimeout(() => {
        navigate(toRelativeUrl(originalUri || sessionStorageRedirectUri || '/', window.location.origin), {
          replace: true,
        });
      }, 0);
    },
    [navigate]
  );

  const handleAuthRequired = useCallback(() => {
    setTimeout(() => {
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      console.log('AppWithOktaSecurityWrapper - onAuthRequired - navigate() to / (was ', originalUri, ')');
      navigate(toRelativeUrl('/', window.location.origin));
    }, 0);
  }, [navigate]);

  // Detect if running in an iframe
  const inIframe = window.self !== window.top;
  const enableEpicEmbeddedView = useEnableEpicEmbeddedView();
  if (!inIframe && enableEpicEmbeddedView) {
    // Redirect into iframe version of itself
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'baseline',
          height: '100vh',
          width: '100%',
          overflow: 'scroll',
        }}
      >
        <iframe title="Medpearl (EPIC)" src="/" width="1162" height="790" style={{ border: 'none' }} />
      </div>
    );
  }

  const app = (
    <StrictMode>
      <App />
    </StrictMode>
  );

  if (smartFhirCookieData || shareVueCookieData) {
    return app;
  }

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri} onAuthRequired={handleAuthRequired}>
      {app}
    </Security>
  );
}

function App() {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const surveyInfo = useAppSelector(selectUserSurveyInfo);
  const flags = useAppSelector(selectUserFlags);
  const [mode, theme, toggleColorModeCallback] = usePaletteMode();
  useCommonAppStartup();
  const dispatch = useAppDispatch();
  const initialNpsValue = !!surveyInfo?.find((x) => x.name === SurveyTypes.nps && x.show);
  const npsSurveyId = surveyInfo?.find((x) => x.name === SurveyTypes.nps)?.id || '';
  const [showNPIPrompt, setShowNPIPrompt] = useState<boolean>(false);
  const [showNps, setShowNps] = useState<boolean>(initialNpsValue);

  let isLegalDisclaimerAccepted = surveyInfo?.some((x) => x.name === SurveyTypes.legalDisclaimer);
  let npiPrompt = surveyInfo?.find((x) => x.name === SurveyTypes.npiPrompt) || null;
  useSmartFhirCookieLogin(); // Login automatically if we have a smartFhir cookie
  useShareVueCookieLogin(); // Login automatically if we have a share-vue cookie
  const canViewCME = useAppSelector(selectCanViewCME);
  // This needs to be elaborated when we implement NPS.
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState<boolean>(false);
  const npsSurveyQuestions = useAppSelector(selectNPSSurvey)?.questions;
  useEffect(() => {
    if (pearlUserInfo && initialNpsValue) {
      setShowNps(initialNpsValue);
      void dispatch(fetchNpsSurveyInfo());
    }
  }, [pearlUserInfo, initialNpsValue, dispatch]);
  useEffect(() => {
    if (pearlUserInfo) {
      void dispatch(fetchSurveyInfo());
    }
  }, [pearlUserInfo, dispatch]);
  useEffect(() => {
    if (pearlUserInfo && isLegalDisclaimerAccepted === false) {
      setShowLegalDisclaimer(true);
    }
  }, [pearlUserInfo, isLegalDisclaimerAccepted]);
  useEffect(() => {
    if (pearlUserInfo?.userId) {
      analyticsIdentify(pearlUserInfo?.userId);
    }
  }, [pearlUserInfo?.userId]);

  // we need to set the data-theme, as algolia autocomplete uses this to identify the theme.
  useEffect(() => {
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  useEffect(() => {
    if (pearlUserInfo && flags?.show_npi && npiPrompt?.show === true) {
      setShowNPIPrompt(true);
    }
  }, [pearlUserInfo, flags?.show_npi, npiPrompt?.show]);

  return (
    <div className="App App-Consumer" data-mode={mode}>
      {/* This adds the survey container to the DOM  */}
      {pearlUserInfo && (
        <>
          {' '}
          <LegalDisclaimerSurvey
            surveyType={SurveyTypes.legalDisclaimer}
            show={showLegalDisclaimer}
            closeDisclaimer={setShowLegalDisclaimer}
          />
          <NPI closeNpiPrompt={setShowNPIPrompt} prompt_id={npiPrompt?.id} show={showNPIPrompt} />
          <NPSSurvey closeNps={setShowNps} show={showNps} questions={npsSurveyQuestions} npsSurveyId={npsSurveyId} />
        </>
      )}
      <DevWarningModal userLoggedIn={!!pearlUserInfo} />
      {/* CssBaseline just gives us a more consistent styling with old browsers.
          It's not vital, but it's minimal and saves us headaches on old browsers.

          See: https://mui.com/components/css-baseline/
          */}
      <CssBaseline />
      <ColorModeContext.Provider value={toggleColorModeCallback}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={5000}
            action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
          >
            {/* SnackbarProvider must be inside ThemeProvider */}
            {/* See https://reactrouter.com/docs/en/v6/getting-started/overview */}
            <SnackbarUtilsConfigurator />
            <Routes>
              <Route path="/unauth" element={<ExternalReviewerView />} />
              <Route path="/:org" element={<Navigate to="/" />} />
              <Route path="/pretendepiclogin" element={<PretendEpicLogin />} />
              <Route path="/pretendsharevuelogin" element={<PretendShareVueLogin />} />
              <Route path="/" element={<TopLayout pearlUserInfo={pearlUserInfo} />}>
                <Route element={<ErrorScreenContainer />}>
                  <Route
                    path=":org/login/callback"
                    element={
                      <LoginCallbackWrapper>
                        <LoginCallback loadingElement={<LoginCallbackLoading />} errorComponent={LoginErrorComponent} />
                      </LoginCallbackWrapper>
                    }
                  />
                  <Route element={<RequiredAuth ready={isEnabledAffiliateAdminForUser()} />}>
                    <Route
                      path="affiliateadmin"
                      element={
                        <Suspense fallback={<Loading variant="compact" />}>
                          <AffiliateAdminDashboard />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route path="smart-fhir" element={<NavigateWithFallback to="/" />} />
                  <Route path="share-vue" element={<NavigateWithFallback to="/" />} />
                  <Route path="admin/logs" element={<ConsoleLog />} />
                  <Route path="admin/downloadfhirpatients" element={<AdminFhirDownloadAnonymous />} />
                  <Route element={<RequiredAuth ready={pearlUserInfo != null} />}>
                    <Route element={<NavigateWithFallback to="/chat" replace />} />
                    {['chat', ':org/chat'].map((path, index) => (
                      <Route
                        key={index}
                        path={path}
                        element={<ChatOrHomeRoute creatorMode={false} searchCategories={true} />}
                      ></Route>
                    ))}
                    <Route index element={<Navigate to="/home" />} />
                    <Route
                      path="home"
                      element={
                        <HomePage creatorMode={false} searchCategories={true} isMuscUser={isMuscUser(pearlUserInfo)} />
                      }
                    />
                    <Route path="favorites" element={<FavoriteTable />} />
                    <Route element={<RequiredAuth ready={canViewCME === true} />}>
                      <Route path="my-cme" element={<CME />} />
                    </Route>
                  </Route>
                  <Route
                    path="*"
                    element={
                      <main style={{ padding: '1rem' }}>
                        <p>Path not recognized!</p>
                      </main>
                    }
                  />
                </Route>
              </Route>
            </Routes>
          </SnackbarProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </div>
  );
}

export default AppWithOktaSecurityWrapper;
